import React, { useState } from "react";
import { Star32Filled, Flash32Filled } from "@fluentui/react-icons";

import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  makeStyles,
  Checkbox,
  DialogSurface,
} from "@fluentui/react-components";

/**
 * Interface définissant les propriétés du composant CarteCredit.
 */
interface CreditProps {
  type: string; // Type de crédit (ex. "62 500 Crédits + 12 500 gratuits!")
  price: string; // Prix du crédit
  description: string; // Description du crédit
  couleur: string; // Couleur de fond de la carte
  boutonTexte: string; // Texte affiché sur le bouton
  boutonCouleur: string; // Couleur du bouton
  fonctionnalites: string[]; // Liste des fonctionnalités incluses
  icone?: any; // Icône optionnelle
  handlePayment: (type: string) => void; // Fonction appelée lors du paiement
}

/**
 * Composant CarteCredit : représente le tableau contenant les offres de crédits
 * Carte: représente le nom d'une composante de fluentUI pour générer des boîtes d'informations
 */
const CarteCredit: React.FC<CreditProps> = (props: CreditProps) => {
  const {
    type,
    price,
    description,
    couleur,
    boutonTexte,
    boutonCouleur,
    fonctionnalites,
    icone,
    handlePayment,
  } = props;

  const styles = useStyles();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [hasConsented, setHasConsented] = useState(false);
  const [isDocumentRead, setIsDocumentRead] = useState(false);
  const [showError, setShowError] = useState(false);

  /**
   * Fonction pour récupérer l'image associée à un offre de crédits.
   * @param type - Offre de crédits
   * @returns Chemin de l'image
   */
  const getImageFromType = (type: string) => {
    switch (type) {
      case "62 500 Crédits + 12 500 gratuits!":
        return "assets/500credits.png";
      case "25 000 Crédits + 1000 gratuits!":
        return "assets/200credits.png";
      case "12 500 Crédits":
        return "assets/100credits.png";
      default:
        return "assets/500credits.png";
    }
  };

  /**
   * Fonction appelée lorsqu'un lien est cliqué.
   */
  const handleLinkClick = () => {
    setIsDocumentRead(true);
  };

  return (
    <div
      className={styles.container}
      style={{ backgroundColor: couleur, borderColor: boutonCouleur }}
    >
      {/* Icônes pour les offres (Éclair, étoile) */}
      <div className={styles.topContainer}>
        <h2 className={styles.title}>{type}</h2>
        {type === "62 500 Crédits + 12 500 gratuits!" && (
          <Flash32Filled className={styles.icon} style={{ color: "black" }} />
        )}
        {type === "25 000 Crédits + 1000 gratuits!" && (
          <Star32Filled className={styles.icon} style={{ color: "black" }} />
        )}
      </div>

      {/* Offre de crédits, prix, image, bouton */}
      <span style={{ fontSize: "2em", fontWeight: "bold", color: "black" }}>${price}</span>
      <p style={{ color: "black" }}>{description}</p>
      <img src={getImageFromType(type)} style={{ width: "200px", marginBottom: "20px" }} />
      <Button
        onClick={() => {
          setIsDialogOpen(false); // SET BACK TO TRUE WHEN LIVE PAYMENTS IS READY
          setShowError(false);
        }}
        disabled // REMOVE DISABLED WHEN LIVE PAYMENTS IS READY
        style={{
          backgroundColor: props.boutonCouleur,
          color: "white",
          borderColor: props.couleur,
          marginTop: "10px",
        }}
      >
        {boutonTexte}
      </Button>

      {/* Pop-up politique de confidentialité */}
      <Dialog open={isDialogOpen} onOpenChange={(_e: any, data: any) => setIsDialogOpen(data.open)}>
        <DialogSurface>
          <DialogTitle>Politique de Confidentialité</DialogTitle>
          <DialogContent>
            <p>
              Votre vie privée est importante pour nous. Veuillez lire notre politique de
              confidentialité attentivement avant de continuer.
            </p>
            <p>
              {/* Chemin pour la politique (On n'en a pas encore, Yves ne l'a pas écrit encore) */}
              <a
                href="C:\Users\Alex\OneDrive - Collège de Bois-de-Boulogne\Cours\H2025\Stages\PluginExcelAI\dummy.pdf"
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleLinkClick}
              >
                Lire notre politique de confidentialité
              </a>
            </p>
            <Checkbox
              label="Je consens à la politique de confidentialité"
              checked={hasConsented}
              disabled={!isDocumentRead}
              onChange={(e: any) => {
                setHasConsented(e.target.checked);
                setShowError(false);
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button
              appearance="primary"
              onClick={() => {
                if (hasConsented) {
                  handlePayment(type);
                  setIsDialogOpen(false);
                  setHasConsented(false);
                  setShowError(false);
                } else {
                  setShowError(true);
                }
              }}
            >
              Continuer
            </Button>
            <Button
              onClick={() => {
                setIsDialogOpen(false);
                setHasConsented(false);
                //setIsDocumentRead(false);
              }}
            >
              Annuler
            </Button>

            {showError && (
              <p style={{ color: "red", fontSize: "0.8em" }}>
                Vous devez accepter la politique de confidentialité pour continuer
              </p>
            )}
          </DialogActions>
        </DialogSurface>
      </Dialog>

      <br />
    </div>
  );
};

export default CarteCredit;

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: "5px",
    borderRadius: "5px",
    border: "1px solid",
    margin: "20px",
  },
  topContainer: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    position: "relative",
    margin: "10px",
  },
  title: {
    flexGrow: 1,
    textAlign: "center",
    margin: 0,
    marginBottom: "10px",
    color: "black",
  },
  icon: {
    position: "absolute",
    right: 0,
    marginRight: "10px",
  },
});
